/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  CREATE_USER,
  CREATE_USER_FAIL,
  CREATE_USER_SUCCESS,
  UPDATE_SELECTED_USER,
  UPDATE_SELECTED_USER_ID,
  UPDATE_USER_LISTING_CURRENT_PAGE,
  UPDATE_USER_LISTING_PAGE_SIZE,
  UPDATE_USER_STATUS,
  UPDATE_USER_STATUS_FAIL,
  UPDATE_USER_STATUS_SUCCESS,
  USER_LOAD,
  USER_LOAD_FAIL,
  USER_LOAD_SUCCESS,
  USER_ROLE_CREATE,
  USER_ROLE_CREATE_FAIL,
  USER_ROLE_CREATE_SUCCESS,
  USER_ROLE_DELETE,
  USER_ROLE_DELETE_FAIL,
  USER_ROLE_DELETE_SUCCESS,
  USER_ROLE_UPDATE,
  USER_ROLE_UPDATE_FAIL,
  USER_ROLE_UPDATE_SUCCESS,
  USER_ROLES_LOAD,
  USER_ROLES_LOAD_FAIL,
  USER_ROLES_LOAD_SUCCESS,
  USERS_FILTER_COUNTRY_VALUE_REMOVE,
  USERS_FILTER_PROVINCE_VALUE_REMOVE,
  USERS_FILTER_VALUE_ADD,
  USERS_FILTER_VALUE_REMOVE,
  USERS_FILTER_VALUE_UPDATE,
  USERS_RESET_PAGINATION_PROPS,
  USERS_RESET_PROPS,
  USERS_SEARCH,
  USERS_SEARCH_FAIL,
  USERS_SEARCH_SUCCESS,
} from "Constants/ActionTypes";
import { IAccessControlUser } from "Interfaces/AccessControl/IAccessControlUser";
import { IUserRole } from "Interfaces/AccessControl/IUserRole";
import { IWriteAccessControlUser } from "Interfaces/AccessControl/IWriteAccessControlUser";
import { IWriteUpdateUserStatus } from "Interfaces/AccessControl/IWriteUpdateUserStatus";
import { IFilterValue } from "Interfaces/IFilterValue";

/**
 * Action for when loading an user.
 * @interface
 */
export interface ILoadUser {
  type: typeof USER_LOAD;
  selectedUserId: number;
}

/**
 * Action for when an user is loaded successfully.
 * @interface
 */
export interface ILoadUserSuccess {
  type: typeof USER_LOAD_SUCCESS;
  user: IAccessControlUser;
}

/**
 * Action for when loading an user failed.
 * @interface
 */
export interface ILoadUserFail {
  type: typeof USER_LOAD_FAIL;
  loadSelectedUserError: string;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IAccessControlFilterValueAdd {
  type: typeof USERS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IAccessControlFilterValueRemove {
  type: typeof USERS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter province value.
 * @interface
 */
export interface IAccessControlFilterProvinceValueRemove {
  type: typeof USERS_FILTER_PROVINCE_VALUE_REMOVE;
}

/**
 * Action for when removing selected filter country value.
 * @interface
 */
export interface IAccessControlFilterCountryValueRemove {
  type: typeof USERS_FILTER_COUNTRY_VALUE_REMOVE;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IAccessControlFilterValueUpdate {
  type: typeof USERS_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for when searching users.
 * @interface
 */
export interface ISearchUsers {
  type: typeof USERS_SEARCH;
  searchText: string;
  tenant: string;
  selectedAccessControlFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
}

/**
 * Action for when users were searched successfully.
 * @interface
 */
export interface ISearchUsersSuccess {
  type: typeof USERS_SEARCH_SUCCESS;
  users: IAccessControlUser[];
  searchPage: number;
  pageSize: number;
  filteredCount: number;
}

/**
 * Action for when loading users failed.
 * @interface
 */
export interface ISearchUsersFail {
  type: typeof USERS_SEARCH_FAIL;
  error: string;
}

/**
 * Action for when resting props.
 * @interface
 */
export interface IResetProps {
  type: typeof USERS_RESET_PROPS;
}

/**
 * Action for when resting pagination props.
 * @interface
 */
export interface IResetPaginationProps {
  type: typeof USERS_RESET_PAGINATION_PROPS;
}

/**
 * Action to create an new user
 * @interface
 */
export interface ICreateUser {
  type: typeof CREATE_USER;
  tenant: string;
  accessUser: IWriteAccessControlUser;
}

/**
 * Action for when creating user successfully.
 * @interface
 */
export interface ICreateUserSuccess {
  type: typeof CREATE_USER_SUCCESS;
  accessUser: IWriteAccessControlUser;
}

/**
 * Action for when creating users failed.
 * @interface
 */
export interface ICreateUserFail {
  type: typeof CREATE_USER_FAIL;
  errorMessage: string;
}

/**
 * Action to update the status of an user
 * @interface
 */
export interface IUpdateUserStatus {
  type: typeof UPDATE_USER_STATUS;
  tenant: string;
  userId: number;
  isActive: boolean;
}

/**
 * Action for when updating user status successfully.
 * @interface
 */
export interface IUpdateUserStatusSuccess {
  type: typeof UPDATE_USER_STATUS_SUCCESS;
  userStatus: IWriteUpdateUserStatus;
}

/**
 * Action for when updating user status failed.
 * @interface
 */
export interface IUpdateUserStatusFail {
  type: typeof UPDATE_USER_STATUS_FAIL;
  updateUserStatusError: string;
}

/**
 * Action for when updating user profile.
 * @interface
 */
export interface IUpdateSelectedUser {
  type: typeof UPDATE_SELECTED_USER;
  selectedUser: IAccessControlUser;
}

/**
 * Action for when setting the selected user id
 * @interface
 */
export interface IUpdateSelectedUserId {
  type: typeof UPDATE_SELECTED_USER_ID;
  selectedUserId: number;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IUpdateCurrentPage {
  type: typeof UPDATE_USER_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IUpdatePageSize {
  type: typeof UPDATE_USER_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action for when loading the user roles.
 * @interface
 */
export interface ILoadUserRoles {
  type: typeof USER_ROLES_LOAD;
  email: string;
  tenant: string;
}

/**
 * Action for when loading the user roles succeed.
 * @interface
 */
export interface ILoadUserRolesSuccess {
  type: typeof USER_ROLES_LOAD_SUCCESS;
  roles: IUserRole[];
}

/**
 * Action for when loading the user roles fail.
 * @interface
 */
export interface ILoadUserRolesFail {
  type: typeof USER_ROLES_LOAD_FAIL;
  message: string;
}

/**
 * Action for when creating a user role.
 * @interface
 */
export interface ICreateUserRole {
  type: typeof USER_ROLE_CREATE;
  tenant: string;
  role: IUserRole;
}

/**
 * Action for when creating a user role succeed.
 * @interface
 */
export interface ICreateUserRoleSuccess {
  type: typeof USER_ROLE_CREATE_SUCCESS;
  role: IUserRole;
}

/**
 * Action for when creating a user role fail.
 * @interface
 */
export interface ICreateUserRoleFail {
  type: typeof USER_ROLE_CREATE_FAIL;
  message: string;
}

/**
 * Action for when updating a user role.
 * @interface
 */
export interface IUpdateUserRole {
  type: typeof USER_ROLE_UPDATE;
  tenant: string;
  role: IUserRole;
}

/**
 * Action for when updating a user role succeed.
 * @interface
 */
export interface IUpdateUserRoleSuccess {
  type: typeof USER_ROLE_UPDATE_SUCCESS;
  role: IUserRole;
}

/**
 * Action for when updating a user role fail.
 * @interface
 */
export interface IUpdateUserRoleFail {
  type: typeof USER_ROLE_UPDATE_FAIL;
  message: string;
}

/**
 * Action for when deleting a user role.
 * @interface
 */
export interface IDeleteUserRole {
  type: typeof USER_ROLE_DELETE;
  roleId: number;
}

/**
 * Action for when deleting a user role succeed.
 * @interface
 */
export interface IDeleteUserRoleSuccess {
  type: typeof USER_ROLE_DELETE_SUCCESS;
  userRoleId: number;
}

/**
 * Action for when deleting a user role fail.
 * @interface
 */
export interface IDeleteUserRoleFail {
  type: typeof USER_ROLE_DELETE_FAIL;
  message: string;
}

/**
 * Action creator for USER_LOAD.
 */
export function loadUser(selectedUserId: number): AccessControlActionTypes {
  return {
    type: USER_LOAD,
    selectedUserId,
  };
}

/**
 * Action creator for USERS_LOAD_SUCCESS.
 * @param users the user profiles successfully loaded.
 */
export function loadUserSuccess(user: IAccessControlUser): AccessControlActionTypes {
  return {
    type: USER_LOAD_SUCCESS,
    user,
  };
}

/**
 * Action creator for USERS_LOAD_FAIL.
 * @param error the error message from the API.
 */
export function loadUserFail(loadSelectedUserError: string): AccessControlActionTypes {
  return {
    type: USER_LOAD_FAIL,
    loadSelectedUserError,
  };
}

/**
 * Action creator for USERS_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addAccessControlFilterValue(filterValue: IFilterValue): AccessControlActionTypes {
  return {
    type: USERS_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for USERS_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeAccessControlFilterValue(
  filterValue: IFilterValue
): AccessControlActionTypes {
  return {
    type: USERS_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for USERS_FILTER_PROVINCE_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeAccessControlFilterProvinceValue(): AccessControlActionTypes {
  return {
    type: USERS_FILTER_PROVINCE_VALUE_REMOVE,
  };
}

/**
 * Action creator for USERS_FILTER_COUNTRY_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeAccessControlFilterCountryValue(): AccessControlActionTypes {
  return {
    type: USERS_FILTER_COUNTRY_VALUE_REMOVE,
  };
}

/**
 * Action creator for USERS_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateAccessControlFilterValue(
  filterValue: IFilterValue
): AccessControlActionTypes {
  return {
    type: USERS_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for USERS_SEARCH.
 */
export function searchUsers(
  searchText: string,
  tenant: string,
  selectedAccessControlFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number
): AccessControlActionTypes {
  return {
    type: USERS_SEARCH,
    searchText,
    tenant,
    selectedAccessControlFilterValues,
    searchPage,
    pageSize,
  };
}

/**
 * Action creator for USERS_SEARCH_SUCCESS.
 * @param users the user profiles successfully searched.
 */
export function searchUsersSuccess(
  users: IAccessControlUser[],
  searchPage: number,
  pageSize: number,
  filteredCount: number
): AccessControlActionTypes {
  return {
    type: USERS_SEARCH_SUCCESS,
    users,
    searchPage,
    pageSize,
    filteredCount,
  };
}

/**
 * Action creator for USERS_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchUsersFail(error: string): AccessControlActionTypes {
  return {
    type: USERS_SEARCH_FAIL,
    error,
  };
}

/**
 * Action creator for ACTIVITY_RESET_PROPS.
 * @returns {AccessControlActionTypes} The action type.
 */
export function resetProps(): AccessControlActionTypes {
  return {
    type: USERS_RESET_PROPS,
  };
}

/**
 * Action creator for ACTIVITY_RESET_PAGINATION_PROPS.
 * @returns {AccessControlActionTypes} The action type.
 */
export function resetPaginationProps(): AccessControlActionTypes {
  return {
    type: USERS_RESET_PAGINATION_PROPS,
  };
}

/**
 * Action creator for CREATE_USER.
 * @param {string} tenant The tenant.
 * @param {IWriteAccessControlUser} accessUser Access user to create.
 * @returns {AccessControlActionTypes} The action type.
 */
export function createUser(
  tenant: string,
  accessUser: IWriteAccessControlUser
): AccessControlActionTypes {
  return {
    type: CREATE_USER,
    tenant: tenant,
    accessUser: accessUser,
  };
}

/**
 * Action creator for CREATE_USER_SUCCESS.
 * @param {IWriteAccessControlUser} accessUser Access user created.
 * @returns {AccessControlActionTypes} The action type.
 */
export function createUserSuccess(accessUser: IWriteAccessControlUser): AccessControlActionTypes {
  return {
    type: CREATE_USER_SUCCESS,
    accessUser: accessUser,
  };
}

/**
 * Action creator for CREATE_USER_FAIL.
 * @param {string} errorMessage The message describing why the status fetch failed.
 * @returns {AccessControlActionTypes} The action type.
 */
export function createUserFail(errorMessage: string): AccessControlActionTypes {
  return {
    type: CREATE_USER_FAIL,
    errorMessage: errorMessage,
  };
}

/**
 * Action creator for UPDATE_USER_STATUS.
 * @param {string} tenant The tenant.
 * @param {number} userId Access user id to update.
 * @param {boolean} isActive Access user status to update.
 * @returns {AccessControlActionTypes} The action type.
 */
export function updateUserStatus(
  tenant: string,
  userId: number,
  isActive: boolean
): AccessControlActionTypes {
  return {
    type: UPDATE_USER_STATUS,
    tenant,
    userId,
    isActive,
  };
}

/**
 * Action creator for UPDATE_USER_STATUS_SUCCESS.
 * @param {IWriteUpdateUserStatus} userStatus Updated user status info.
 * @returns {AccessControlActionTypes} The action type.
 */
export function updateUserStatusSuccess(
  userStatus: IWriteUpdateUserStatus
): AccessControlActionTypes {
  return {
    type: UPDATE_USER_STATUS_SUCCESS,
    userStatus,
  };
}

/**
 * Action creator for UPDATE_USER_STATUS_FAIL.
 * @param error the error message from the API.
 */
export function updateUserStatusFail(updateUserStatusError: string): AccessControlActionTypes {
  return {
    type: UPDATE_USER_STATUS_FAIL,
    updateUserStatusError,
  };
}

/**
 * Action creator for UPDATE_SELECTED_USER.
 */
export function updateSelectedUser(selectedUser: IAccessControlUser): AccessControlActionTypes {
  return {
    type: UPDATE_SELECTED_USER,
    selectedUser,
  };
}

/**
 * Action creator for UPDATE_SELECTED_USER_ID.
 */
export function updateSelectedUserId(selectedUserId: number): AccessControlActionTypes {
  return {
    type: UPDATE_SELECTED_USER_ID,
    selectedUserId,
  };
}

/**
 * Action creator for UPDATE_USER_LISTING_CURRENT_PAGE.
 */
export function updateCurrentPage(currentPage: number): AccessControlActionTypes {
  return {
    type: UPDATE_USER_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_USER_LISTING_PAGE_SIZE.
 */
export function updatePageSize(pageSize: number): AccessControlActionTypes {
  return {
    type: UPDATE_USER_LISTING_PAGE_SIZE,
    pageSize,
  };
}

/**
 * Action create for USER_ROLES_LOAD
 */
export function loadUserRoles(tenant: string, email: string): AccessControlActionTypes {
  return {
    type: USER_ROLES_LOAD,
    email,
    tenant,
  };
}

/**
 * Action create for USER_ROLES_LOAD_SUCCESS
 */
export function loadUserRolesSuccess(roles: IUserRole[]): AccessControlActionTypes {
  return {
    type: USER_ROLES_LOAD_SUCCESS,
    roles,
  };
}

/**
 * Action create for USER_ROLES_LOAD_FAIL
 */
export function loadUserRolesFail(message: string): AccessControlActionTypes {
  return {
    type: USER_ROLES_LOAD_FAIL,
    message,
  };
}

/**
 * Action create for USER_ROLES_LOAD
 */
export function deleteUserRole(roleId: number): AccessControlActionTypes {
  return {
    type: USER_ROLE_DELETE,
    roleId,
  };
}

/**
 * Action create for USER_ROLE_CREATE
 */
export function createUserRole(tenant: string, role: IUserRole): AccessControlActionTypes {
  return {
    type: USER_ROLE_CREATE,
    tenant,
    role,
  };
}

/**
 * Action create for USER_ROLE_CREATE_SUCCESS
 */
export function createUserRoleSuccess(role: IUserRole): AccessControlActionTypes {
  return {
    type: USER_ROLE_CREATE_SUCCESS,
    role,
  };
}

/**
 * Action create for USER_ROLE_CREATE_FAIL
 */
export function createUserRoleFail(message: string): AccessControlActionTypes {
  return {
    type: USER_ROLE_CREATE_FAIL,
    message,
  };
}

/**
 * Action create for USER_ROLE_UPDATE
 */
export function updateUserRole(tenant: string, role: IUserRole): AccessControlActionTypes {
  return {
    type: USER_ROLE_UPDATE,
    tenant,
    role,
  };
}

/**
 * Action create for USER_ROLE_UPDATE_SUCCESS
 */
export function updateUserRoleSuccess(role: IUserRole): AccessControlActionTypes {
  return {
    type: USER_ROLE_UPDATE_SUCCESS,
    role,
  };
}

/**
 * Action create for USER_ROLE_UPDATE_FAIL
 */
export function updateUserRoleFail(message: string): AccessControlActionTypes {
  return {
    type: USER_ROLE_UPDATE_FAIL,
    message,
  };
}

/**
 * Action create for USER_ROLE_DELETE_SUCCESS
 */
export function deleteUserRoleSuccess(userRoleId: number): AccessControlActionTypes {
  return {
    type: USER_ROLE_DELETE_SUCCESS,
    userRoleId,
  };
}

/**
 * Action create for USER_ROLES_LOAD_FAIL
 */
export function deleteUserRoleFail(message: string): AccessControlActionTypes {
  return {
    type: USER_ROLE_DELETE_FAIL,
    message,
  };
}

export type AccessControlActionTypes =
  | ILoadUser
  | ILoadUserSuccess
  | ILoadUserFail
  | IAccessControlFilterValueAdd
  | IAccessControlFilterValueRemove
  | IAccessControlFilterProvinceValueRemove
  | IAccessControlFilterCountryValueRemove
  | IAccessControlFilterValueUpdate
  | ISearchUsers
  | ISearchUsersSuccess
  | ISearchUsersFail
  | IResetProps
  | IResetPaginationProps
  | ICreateUser
  | ICreateUserSuccess
  | ICreateUserFail
  | IUpdateUserStatus
  | IUpdateUserStatusSuccess
  | IUpdateUserStatusFail
  | IUpdateSelectedUser
  | IUpdateSelectedUserId
  | IUpdateCurrentPage
  | IUpdatePageSize
  | ILoadUserRoles
  | ILoadUserRolesSuccess
  | ILoadUserRolesFail
  | ICreateUserRole
  | ICreateUserRoleSuccess
  | ICreateUserRoleFail
  | IUpdateUserRole
  | IUpdateUserRoleSuccess
  | IUpdateUserRoleFail
  | IDeleteUserRole
  | IDeleteUserRoleSuccess
  | IDeleteUserRoleFail;
